import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { useTrips } from "@/hooks/useTrips";
import { useExpenses, INITIAL_EXPENSE_TYPES } from "@/hooks/useExpenses";
import { useVehicles } from "@/hooks/useVehicles";
import { format, parseISO } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";

const VEHICLE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--destructive))",
  "hsl(210, 70%, 55%)",
  "hsl(150, 60%, 45%)",
  "hsl(280, 60%, 55%)",
  "hsl(30, 80%, 55%)",
  "hsl(340, 70%, 55%)",
  "hsl(180, 60%, 45%)",
  "hsl(60, 70%, 45%)",
  "hsl(240, 50%, 60%)",
];

export const NetProfitMarginChart = () => {
  const { trips, isLoading: tripsLoading } = useTrips();
  const { expenses, isLoading: expensesLoading } = useExpenses();
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();

  const isLoading = tripsLoading || expensesLoading || vehiclesLoading;

  const { chartData, vehicleNames } = useMemo(() => {
    if (!vehicles?.length || !trips?.length) return { chartData: [], vehicleNames: [] };

    // Find vehicles that have trips
    const vehiclesWithTrips = vehicles.filter((v) =>
      trips.some((t) => t.vehicle_id === v.id && t.trip_start)
    );

    if (!vehiclesWithTrips.length) return { chartData: [], vehicleNames: [] };

    const allMonths = new Set<string>();

    // Earnings by vehicle by month
    const earningsByVehicleMonth: Record<string, Record<string, number>> = {};
    for (const vehicle of vehiclesWithTrips) {
      const byMonth: Record<string, number> = {};
      const vTrips = trips.filter(
        (t) => t.vehicle_id === vehicle.id && t.trip_start && !t.trip_status?.toLowerCase().includes("cancel")
      );
      for (const trip of vTrips) {
        try {
          const monthKey = format(parseISO(trip.trip_start!), "yyyy-MM");
          byMonth[monthKey] = (byMonth[monthKey] || 0) + (trip.total_earnings || trip.revenue || 0);
          allMonths.add(monthKey);
        } catch { /* skip */ }
      }
      earningsByVehicleMonth[vehicle.id] = byMonth;
    }

    // Ongoing expenses by vehicle by month (exclude initial expense types)
    const expensesByVehicleMonth: Record<string, Record<string, number>> = {};
    for (const vehicle of vehiclesWithTrips) {
      const byMonth: Record<string, number> = {};
      const vExpenses = (expenses || []).filter(
        (e) => e.vehicle_id === vehicle.id && !INITIAL_EXPENSE_TYPES.includes(e.expense_type || "")
      );
      for (const expense of vExpenses) {
        try {
          const monthKey = format(parseISO(expense.expense_date), "yyyy-MM");
          byMonth[monthKey] = (byMonth[monthKey] || 0) + (expense.amount || 0);
        } catch { /* skip */ }
      }
      expensesByVehicleMonth[vehicle.id] = byMonth;
    }

    const sortedMonths = Array.from(allMonths).sort();
    const names = vehiclesWithTrips.map(
      (v) => v.name || `${v.year} ${v.make} ${v.model}`
    );

    const data = sortedMonths.map((monthKey) => {
      const entry: Record<string, string | number> = {
        month: format(parseISO(monthKey + "-01"), "MMM yyyy"),
      };

      vehiclesWithTrips.forEach((vehicle, idx) => {
        const earnings = earningsByVehicleMonth[vehicle.id]?.[monthKey] || 0;
        const expenseTotal = expensesByVehicleMonth[vehicle.id]?.[monthKey] || 0;
        const margin = earnings > 0 ? Math.round(((earnings - expenseTotal) / earnings) * 100) : 0;
        entry[names[idx]] = margin;
      });

      return entry;
    });

    return { chartData: data, vehicleNames: names };
  }, [vehicles, trips, expenses]);

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Net Profit Margin</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Net Profit Margin</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="month"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  color: "hsl(var(--popover-foreground))",
                }}
                formatter={(value: number, name: string) => [`${value}% — ${name}`, undefined]}
              />
              <Legend />
              {vehicleNames.map((name, idx) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={VEHICLE_COLORS[idx % VEHICLE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No profit data yet. Import trips and expenses to see margins.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
